import type { ReactNode } from "react";
import {
  ArrowUpRight,
  BatteryFull,
  ChevronLeft,
  Globe,
  Lock,
  MoreVertical,
  Signal,
  Wifi,
  X,
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
  fillVariables,
  smsSegments,
  type CampaignDraft,
  type ChannelKey,
  type ProgramContext,
} from "./model";

type PreviewProps = {
  draft: CampaignDraft;
  ctx: ProgramContext;
  values: Record<string, string>;
};

function Phone({ children, dark }: { children: ReactNode; dark?: boolean }) {
  return (
    <div className="mx-auto w-full max-w-[290px] rounded-[2.2rem] border-[10px] border-slate-900 bg-slate-900 shadow-xl">
      <div
        className={cn(
          "flex items-center justify-between rounded-t-[1.5rem] px-5 pb-1 pt-2 text-[10px] font-semibold",
          dark ? "bg-[#075E54] text-white" : "bg-white text-slate-900",
        )}
      >
        <span>9:41</span>
        <span className="flex items-center gap-1">
          <Signal className="h-3 w-3" />
          <Wifi className="h-3 w-3" />
          <BatteryFull className="h-3.5 w-3.5" />
        </span>
      </div>
      <div className="h-[470px] overflow-hidden rounded-b-[1.5rem]">{children}</div>
    </div>
  );
}

function Placeholder({ children }: { children: ReactNode }) {
  return <span className="italic text-slate-400">{children}</span>;
}

function WhatsAppPreview({ draft, ctx, values }: PreviewProps) {
  const c = draft.content.whatsapp;
  const fill = (text: string) => fillVariables(text, values);
  return (
    <Phone dark>
      <div className="flex items-center gap-2 bg-[#075E54] px-3 pb-2.5 text-white">
        <ChevronLeft className="h-4 w-4" />
        <div
          className="flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold"
          style={{ backgroundColor: ctx.brandColor }}
        >
          {ctx.name.slice(0, 1)}
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold">{ctx.name}</p>
          <p className="text-[10px] text-white/70">Business account</p>
        </div>
        <MoreVertical className="h-4 w-4" />
      </div>
      <div className="h-full bg-[#ECE5DD] p-3">
        <div className="mx-auto mb-3 w-fit rounded-md bg-white/80 px-2 py-0.5 text-[10px] text-slate-500">
          Today
        </div>
        <div className="max-w-[88%] rounded-lg rounded-tl-none bg-white p-2.5 text-[13px] leading-snug text-slate-900 shadow-sm">
          {c.header ? <p className="mb-1 font-semibold">{fill(c.header)}</p> : null}
          <p className="whitespace-pre-wrap break-words">
            {c.body ? fill(c.body) : <Placeholder>Your message appears here</Placeholder>}
          </p>
          {c.footer ? <p className="mt-1.5 text-[11px] text-slate-500">{c.footer}</p> : null}
          <p className="mt-1 text-right text-[10px] text-slate-400">10:00</p>
        </div>
        {c.buttonLabel ? (
          <div className="mt-1 max-w-[88%] rounded-lg bg-white py-2 text-center text-[13px] font-medium text-[#128C7E] shadow-sm">
            <ArrowUpRight className="mr-1 inline h-3.5 w-3.5" />
            {c.buttonLabel}
          </div>
        ) : null}
      </div>
    </Phone>
  );
}

function SmsPreview({ draft, ctx, values }: PreviewProps) {
  const text = fillVariables(draft.content.sms.body, values);
  const info = smsSegments(text);
  return (
    <div className="space-y-2">
      <Phone>
        <div className="border-b border-slate-200 bg-white px-3 pb-2 text-center">
          <div className="mx-auto flex h-9 w-9 items-center justify-center rounded-full bg-slate-200 text-xs font-semibold text-slate-600">
            {ctx.smsSender.slice(0, 2)}
          </div>
          <p className="mt-1 text-xs font-medium text-slate-900">{ctx.smsSender}</p>
        </div>
        <div className="h-full bg-white p-3">
          <p className="mb-2 text-center text-[10px] text-slate-400">Text message · Today 10:00</p>
          <div className="max-w-[85%] whitespace-pre-wrap break-words rounded-2xl rounded-bl-md bg-slate-200 px-3 py-2 text-[13px] leading-snug text-slate-900">
            {text || <Placeholder>Your text appears here</Placeholder>}
          </div>
        </div>
      </Phone>
      <p
        className={cn(
          "text-center text-xs",
          info.segments > 3 ? "text-red-600" : info.segments > 1 ? "text-amber-700" : "text-slate-500",
        )}
      >
        {info.length} characters · {info.segments} part{info.segments === 1 ? "" : "s"}
        {info.unicode ? " · Unicode" : ""}
      </p>
    </div>
  );
}

function EmailPreview({ draft, ctx, values }: PreviewProps) {
  const c = draft.content.email;
  const fill = (text: string) => fillVariables(text, values);
  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl">
      <div className="border-b border-slate-200 bg-slate-50 px-4 py-3">
        <div className="flex items-baseline justify-between gap-2">
          <p className="truncate text-sm font-semibold text-slate-900">{c.fromName || ctx.name}</p>
          <span className="shrink-0 text-[11px] text-slate-400">10:00</span>
        </div>
        <p className="truncate text-sm text-slate-800">
          {c.subject ? fill(c.subject) : <Placeholder>Subject line</Placeholder>}
        </p>
        <p className="truncate text-xs text-slate-500">
          {c.preheader ? fill(c.preheader) : <Placeholder>Preview text shown in the inbox</Placeholder>}
        </p>
        <p className="mt-1 text-[11px] text-slate-400">From {ctx.fromEmail}</p>
      </div>
      <div className="max-h-[420px] overflow-y-auto bg-slate-100 p-4">
        <div className="mx-auto max-w-[420px] overflow-hidden rounded-lg bg-white">
          <div className="px-5 py-4 text-white" style={{ backgroundColor: ctx.brandColor }}>
            <p className="text-sm font-semibold">{ctx.name}</p>
          </div>
          <div className="space-y-3 px-5 py-5">
            <h3 className="text-lg font-bold leading-snug text-slate-900">
              {c.heading ? fill(c.heading) : <Placeholder>Email heading</Placeholder>}
            </h3>
            <p className="whitespace-pre-wrap break-words text-sm leading-relaxed text-slate-700">
              {c.body ? fill(c.body) : <Placeholder>Email body</Placeholder>}
            </p>
            {c.ctaLabel ? (
              <span
                className="inline-block rounded-md px-4 py-2 text-sm font-semibold text-white"
                style={{ backgroundColor: ctx.brandColor }}
              >
                {c.ctaLabel}
              </span>
            ) : null}
          </div>
          <div className="border-t border-slate-100 px-5 py-3 text-[11px] text-slate-400">
            You receive this because you are a {ctx.name} member. Unsubscribe
          </div>
        </div>
      </div>
    </div>
  );
}

function InAppPreview({ draft, ctx, values }: PreviewProps) {
  const c = draft.content.inapp;
  const fill = (text: string) => fillVariables(text, values);
  return (
    <Phone>
      <div className="relative h-full bg-slate-100">
        <div className="space-y-2 p-3 opacity-50">
          <div className="h-24 rounded-xl bg-white" />
          <div className="h-12 rounded-xl bg-white" />
          <div className="h-12 rounded-xl bg-white" />
          <div className="h-12 rounded-xl bg-white" />
        </div>
        <div className="absolute inset-0 flex items-end bg-slate-900/40 p-3">
          <div className="w-full overflow-hidden rounded-2xl bg-white shadow-lg">
            <div className="flex h-20 items-center justify-center text-2xl font-bold text-white" style={{ backgroundColor: ctx.brandColor }}>
              {values.bonus}
            </div>
            <div className="relative space-y-2 p-4">
              <X className="absolute right-3 top-3 h-4 w-4 text-slate-400" />
              <p className="pr-5 text-base font-semibold text-slate-900">
                {c.title ? fill(c.title) : <Placeholder>Card title</Placeholder>}
              </p>
              <p className="whitespace-pre-wrap text-sm text-slate-600">{fill(c.body)}</p>
              {c.ctaLabel ? (
                <span
                  className="block rounded-lg py-2 text-center text-sm font-semibold text-white"
                  style={{ backgroundColor: ctx.brandColor }}
                >
                  {c.ctaLabel}
                </span>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </Phone>
  );
}

function WebPreview({ draft, ctx, values }: PreviewProps) {
  const c = draft.content.web;
  const fill = (text: string) => fillVariables(text, values);
  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl">
      <div className="flex items-center gap-2 border-b border-slate-200 bg-slate-100 px-3 py-2">
        <span className="flex gap-1">
          <span className="h-2.5 w-2.5 rounded-full bg-red-400" />
          <span className="h-2.5 w-2.5 rounded-full bg-amber-400" />
          <span className="h-2.5 w-2.5 rounded-full bg-green-400" />
        </span>
        <span className="flex min-w-0 flex-1 items-center gap-1 rounded-md bg-white px-2 py-1 text-[11px] text-slate-500">
          <Lock className="h-3 w-3 shrink-0" />
          <span className="truncate">members.{ctx.name.toLowerCase().replace(/[^a-z0-9]+/g, "")}.com</span>
        </span>
      </div>
      <div
        className="flex flex-wrap items-center gap-3 px-4 py-3 text-white"
        style={{ backgroundColor: ctx.brandColor }}
      >
        <Globe className="h-4 w-4 shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold">
            {c.headline ? fill(c.headline) : <span className="italic text-white/70">Banner headline</span>}
          </p>
          <p className="text-xs text-white/85">{fill(c.body)}</p>
        </div>
        {c.ctaLabel ? (
          <span className="rounded-md bg-white px-3 py-1.5 text-xs font-semibold" style={{ color: ctx.brandColor }}>
            {c.ctaLabel}
          </span>
        ) : null}
      </div>
      <div className="space-y-2 p-4">
        <div className="h-6 w-1/2 rounded bg-slate-100" />
        <div className="grid grid-cols-3 gap-2">
          <div className="h-20 rounded bg-slate-100" />
          <div className="h-20 rounded bg-slate-100" />
          <div className="h-20 rounded bg-slate-100" />
        </div>
      </div>
    </div>
  );
}

const PREVIEWS: Record<ChannelKey, (props: PreviewProps) => JSX.Element> = {
  whatsapp: WhatsAppPreview,
  sms: SmsPreview,
  email: EmailPreview,
  inapp: InAppPreview,
  web: WebPreview,
};

export function ChannelPreview({ channel, ...props }: PreviewProps & { channel: ChannelKey }) {
  const Preview = PREVIEWS[channel];
  return <Preview {...props} />;
}
